// Import icon font system
import { iconFont } from './icon-fonts.js';

// Store current station context for refresh operations
let currentStationContext = null;
let currentStationLine = null;

const LINE_COLORS = {
    default: "#da251d",
    line1: "#FFD320", // Yellow
    line1University: "#FFD320", // Yellow
    line1Yonge: "#FFD320", // Yellow
    line2: "#00B04F", // Green
    line3: "#00a4e3", // Blue
    line4: "#a21a68", // Purple
    line5: "#eb8738", // Orangeish
    line6: "#969594", // Distopian Grey
};

const ROUTE_KEYS = {
    line1University: "Line 1 - Yonge-University",
    line1Yonge: "Line 1 - Yonge-University",
    line2: "Line 2 - Bloor - Danforth",
    line4: "Line 4 - Sheppart",
    line5: "Line 5 - Eglinton LRT",
    line6: "Line 6 - Finch West LRT"
};

// ─── Bike Share ───────────────────────────────────────────────────────────────

// Module-level cache for bike-station-map.json so we only fetch it once per page load
let _bikeStationMapCache = null;

/**
 * Build a rendered card for a bike share station.
 * Used for both updating existing cards and replacing empty ones with fallbacks.
 *
 * @param {Object}  bikeStation - Static station info (from map JSON)
 * @param {Object}  liveData    - Live availability data from BikeShareAPI
 * @param {boolean} isFallback  - Whether this is replacing an empty station
 * @returns {string} - Inner HTML for the card container
 */
function renderBikeStationCard(bikeStation, liveData, isFallback = false) {
    const googleMapsUrl = `https://www.google.com/maps?q=${bikeStation.lat},${bikeStation.lon}`;
    const bikesAvailable = liveData?.num_bikes_available ?? 0;
    const ebike = liveData?.num_bikes_available_types?.ebike ?? 0;

    let bikesClass = 'text-xs text-red-500';
    if (bikesAvailable !== 0) {
        bikesClass = 'text-xs text-green-600 font-medium';
    }
    
    let bikesText = `${bikesAvailable}/${bikeStation.capacity} available`;
    if (ebike > 0) bikesText += ` (${ebike} ⚡)`;
    
    // For fallbacks, show distance instead of address since it's not one of the
    // original 2 shown — gives the user useful context that it's a bit further
    const subtitleText = isFallback
        ? `${bikeStation.distance_meters}m away`
        : bikeStation.address;

    return `<div id="bike-station-${bikeStation.station_id}" class="bg-gray-50 rounded border ml-4 mt-1" style="padding: 0.4rem;">
        <div class="flex items-center justify-between">
            <div class="flex-1">
                <div class="text-xs font-medium text-gray-800">${bikeStation.name}</div>
                <div class="text-xs text-gray-600">${subtitleText}</div>
                <div class="flex items-center gap-3 mt-1">
                    <span class="${bikesClass}">${bikesText}</span>
                    ${bikeStation.is_charging_station ? '<span class="text-xs text-green-600">⚡ E-bikes</span>' : ''}
                </div>
            </div>
            <a href="${googleMapsUrl}" target="_blank" class="ml-2 text-gray-500 hover:text-blue-600 transition-colors" title="View location on Google Maps">
                <i class="fa-solid fa-map-location-dot"></i>
            </a>
        </div>
      </div>`;
}

/**
 * Async enrichment: fetches live bike availability and updates the rendered
 * station cards in the drawer. If a station has 0 bikes, walks down the
 * pre-computed proximity list (from bike-station-map.json) to find the next
 * available station and swaps the card out.
 *
 * @param {Object} subwayStation   - The subway station object (has .name for map lookup)
 * @param {Array}  initialStations - The 2 pre-rendered nearest_bikeshare stations
 */
async function enrichBikeShareAvailability(subwayStation, initialStations) {
    if (!window.BikeShareAPI) {
        window.BikeShareAPI
    }

    try {
        console.log(subwayStation, initialStations)
        // 1. Get the full proximity pool for this subway station from the map JSON
        const stationMap = window.bikeShareData;
        const proximityPool = stationMap[subwayStation.name];

        console.log(proximityPool)
        if (!proximityPool || proximityPool.length === 0) {
            // No bike stations in map for this subway station — show static capacity
            initialStations.forEach((bikeStation, index) => {
                const bikesEl = document.getElementById(`bike-station-${bikeStation.station_id || index}-bikes`);
                if (bikesEl) {
                    bikesEl.textContent = `${bikeStation.capacity} docks`;
                    bikesEl.className = 'text-xs text-gray-500';
                }
            });
            return;
        }

        // 2. Fetch live status for every station in the proximity pool at once
        //    so we only trigger one underlying API call (BikeShareAPI caches internally)
        const data = await window.BikeShareAPI.fetchStationStatus();
        ///console.log(data);

        let liveDataMap = {};
        proximityPool.forEach(bikeStation => {
            const station = data.data.stations.find(
                s => s.station_id === bikeStation.station_id
            );
            if (station) liveDataMap[bikeStation.station_id] = station;
        });
       // console.log(liveDataMap);

        liveDataMap = Object.fromEntries(
            Object.values(liveDataMap)
                .filter(station => station.num_bikes_available > 0)
                .map(station => [station.station_id, station]) // [key, value]
            );
        //console.log(liveDataMap);

        // 4. For each initially rendered card, check live availability and
        //    either update it in place or swap it for the next available station
        let ids = [];
        let content = "";
        const MAX_STATIONS = 3
        proximityPool.forEach((data, index) => {
            if (liveDataMap[data.station_id] && ids.length < 3 && data.distance_meters < 300) {
                ids.push(data.station_id);
                content += renderBikeStationCard(data, liveDataMap[data.station_id]);
            }
        });

        if (content == "" || ids.length < MAX_STATIONS) {
            proximityPool.forEach((data) => {
                if (ids.length < 2 && data.distance_meters < 250 && !ids.includes(data.station_id)) {
                    content += renderBikeStationCard(data);
                }
            });
        }

        const container = document.getElementById('bike-stations-container');
        container.innerHTML = content;


    } catch (err) {
        console.warn('[BikeShare] Live availability enrichment failed, showing static data', err);
        // Graceful fallback — show static capacity from the pre-rendered data
        initialStations.forEach((bikeStation, index) => {
            const bikesEl = document.getElementById(`bike-station-${bikeStation.station_id || index}-bikes`);
            if (bikesEl) {
                bikesEl.textContent = `${bikeStation.capacity} docks`;
                bikesEl.className = 'text-xs text-gray-500';
            }
        });
    }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getLineColor(lineName) {
    if (typeof lineName !== 'string') {
        return LINE_COLORS.default;
    }

    const match = lineName.match(/\bLine\s*(\d)\b/i);
    if (!match) {
        return LINE_COLORS.default;
    }

    const lineKey = `line${match[1]}`;
    return LINE_COLORS[lineKey] || LINE_COLORS.default;
}

function getStationOwnLine(station) {
    for (const [lineKey, { label }] of Object.entries(ROUTE_KEYS)) {
        const line = window[lineKey];
        if (!line?.stations) continue;

        if (line.stations.some(s => s.name === station.name)) {
            console.log(`🔍 ${station.name} found on ${label}`);
            return label;
        }
    }

    console.log(`⚠️ ${station.name} not found on any line`);
    return null;
}

// ─── Initialisation ───────────────────────────────────────────────────────────

function initializeStationUI() {
    loadStationData();
}

function loadStationData() {
    try {
        window.myData = window.stationData;

        let totalStations = 0;

        for (const lineKey of Object.keys(ROUTE_KEYS)) {
            const line = window[lineKey];
            if (line?.stations) {
                totalStations += line.stations.length;
            }
        }

        console.log("TTC data loaded successfully from embedded data");
        console.log(`Loaded ${totalStations} stations across ${Object.keys(ROUTE_KEYS).length - 1} lines`);

        if (typeof window.generateCriticalAlerts === 'function') {
            window.generateCriticalAlerts();
        }

        console.log('Checking for generateLineMessages function:', typeof window.generateLineMessages);
        if (typeof window.generateLineMessages === 'function') {
            console.log('Calling generateLineMessages...');
            window.generateLineMessages();
        } else {
            console.log('generateLineMessages function not available');
        }

        if (typeof drawMap === "function") {
            drawMap();
        } else if (typeof drawResponsiveMap === "function") {
            drawResponsiveMap();
        }

        setTimeout(() => {
            if (window.updateMobileLineVisibility) {
                window.updateMobileLineVisibility();
            }
        }, 100);
    } catch (error) {
        console.error("Failed to load TTC data:", error);
        const mapContainer = document.getElementById("mapContainer");
        if (mapContainer) {
            mapContainer.innerHTML = `
          <div class="flex flex-col items-center justify-center py-12 text-center">
            ${iconFont('warning', 'text-4xl sm:text-6xl text-red-500 mb-4')}
            <h3 class="text-lg sm:text-xl font-semibold text-red-600 mb-2">Failed to Load TTC Data</h3>
            <p class="text-gray-600 mb-4 text-sm sm:text-base">Error: ${error.message}</p>
            <button onclick="location.reload()" class="flex items-center gap-2 px-4 py-2 sm:px-6 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base">
              ${iconFont('schedule', 'text-sm')}
              Retry
            </button>
          </div>
        `;
        }
    }
}

// ─── Station line color ───────────────────────────────────────────────────────

function getStationLineColor(station) {
    for (const lineKey of Object.keys(ROUTE_KEYS)) {
        const line = window[lineKey];
        if (!line?.stations) continue;

        if (line.stations.includes(station)) {
            const colorKey = lineKey.startsWith('line1') ? 'line1' : lineKey;
            return LINE_COLORS[colorKey] || LINE_COLORS.default;
        }
    }

    const SPECIAL_STATIONS = {
        "Spadina": "line2",
        "St George": "line2",
        "Sheppard-Yonge": "line4",
    };

    if (SPECIAL_STATIONS[station.name]) {
        const lineKey = SPECIAL_STATIONS[station.name];
        const line = window[lineKey];
        if (line?.stations?.some(s => s.name === station.name)) {
            return LINE_COLORS[lineKey];
        }
    }

    for (const lineKey of Object.keys(ROUTE_KEYS)) {
        const line = window[lineKey];
        if (!line?.stations) continue;

        if (line.stations.some(s => s.name === station.name)) {
            const colorKey = lineKey.startsWith("line1") ? "line1" : lineKey;
            return LINE_COLORS[colorKey] || LINE_COLORS.default;
        }
    }

    return LINE_COLORS.line1;
}

// ─── Accessibility check ──────────────────────────────────────────────────────

function hasCriticalAccessibilityIssue(station) {
    const mapStationNameForAlerts = (stationName, lineData) => {
        if (
            stationName === "Bloor" &&
            lineData &&
            lineData.name &&
            lineData.name.includes("Line 1")
        ) {
            return "Bloor-Yonge";
        }
        if (
            stationName === "Yonge" &&
            lineData &&
            lineData.name &&
            lineData.name.includes("Line 2")
        ) {
            return "Bloor-Yonge";
        }
        return stationName;
    };

    const hasCriticalAccessibilityInLine = (lineKey, stationName) => {
        if (window.alertData && window.alertData[lineKey] && window.alertData[lineKey].alerts) {
            const alertsKey = mapStationNameForAlerts(stationName, { name: lineKey });

            if (window.alertData[lineKey].alerts[alertsKey]) {
                const criticalAccessibilityAlerts = window.alertData[lineKey].alerts[alertsKey].filter(
                    (alert) => alert.type === "ACCESSIBILITY_ISSUE" &&
                        (alert.severity === "Critical" || alert.severity === "CRITICAL")
                );
                if (criticalAccessibilityAlerts.length > 0) {
                    return true;
                }
            }
        }
        return false;
    };

    for (const lineName of Object.keys(ROUTE_KEYS)) {
        const line = window[lineName];
        if (!line?.stations) continue;

        if (line.stations.some(s => s === station)) {
            return hasCriticalAccessibilityInLine(lineName, station.name);
        }
    }

    return false;
}

// ─── Station drawer ───────────────────────────────────────────────────────────

function showStationDrawer(station, svgId = null) {
    const drawer = document.getElementById("stationDrawer");
    const overlay = document.getElementById("drawerOverlay");
    const title = document.getElementById("stationDrawerTitle");
    const featuresContainer = document.getElementById("stationDrawerFeatures");
    const content = document.getElementById("stationDrawerContent");

    window.currentStationSvgId = svgId;

    const lineColor = getStationLineColor(station);
    const isTransferStation = station.connections && station.connections.length > 0;
    const isUnion = station.name === "Union";

    let stationNameSpan;

    if (isTransferStation && !isUnion) {
        let topLineColor, bottomLineColor;
        const hasLine2Connection = station.connections.some(conn => conn.includes("Line 2"));

        if (hasLine2Connection) {
            topLineColor = LINE_COLORS.line2;
            bottomLineColor = lineColor;
        } else {
            topLineColor = getLineColor(station.connections[0]);
            bottomLineColor = lineColor;
        }

        stationNameSpan = `
      <span class="station-name-underline" style="border-bottom-color: ${bottomLineColor};">
        ${station.name}
        <span class="station-name-transfer-underline" style="background-color: ${topLineColor};"></span>
      </span>
    `;
    } else {
        stationNameSpan = `<span class="station-name-underline" style="border-bottom-color: ${lineColor};">${station.name}</span>`;
    }

    const stationUrl = generateStationUrl(station.name);
    const hasAccessibleFeature = station.features && station.features.includes('Accessible');
    const hasCriticalIssue = hasCriticalAccessibilityIssue(station);
    const isAccessible = hasAccessibleFeature && !hasCriticalIssue;

    const stationNotAccessibleElement = document.getElementById('stationNotAccessible');
    if (stationNotAccessibleElement) {
        stationNotAccessibleElement.style.display = 'inline-block';
        if (isAccessible) {
            stationNotAccessibleElement.title = 'Station is accessible';
            const banIcon = stationNotAccessibleElement.querySelector('.fa-ban');
            if (banIcon) banIcon.style.display = 'none';
        } else {
            const reason = hasCriticalIssue ? 'Station has critical elevator issues' : 'Station is not accessible';
            stationNotAccessibleElement.title = reason;
            const banIcon = stationNotAccessibleElement.querySelector('.fa-ban');
            if (banIcon) banIcon.style.display = 'inline-block';
        }
    }

    const accessibilityTitle = isAccessible ? 'Station is accessible' :
        (hasCriticalIssue ? 'Station has critical elevator issues' : 'Station is not accessible');

    const accessibilityIconHtml = `
        <span id="stationNotAccessible" class="fa-stack fa-sm ml-2" style="display: inline-block;" title="${accessibilityTitle}">
            <i class="fa-solid fa-wheelchair fa-stack-1x text-blue-600"></i>
            ${!isAccessible ? '<i class="fa-solid fa-ban fa-stack-2x text-red-600"></i>' : ''}
        </span>
    `;

    title.innerHTML = `
    <span style="color: ${lineColor};">${iconFont('train', 'text-xl')}</span>
    ${stationNameSpan}
    ${accessibilityIconHtml}
    <a href="${stationUrl}" target="_blank" class="ml-2 text-gray-500 hover:text-blue-600 transition-colors" title="View station details on TTC website">
      <i class="fa-solid fa-link"></i>
    </a>
  `;

    drawer.style.setProperty('--line-color', lineColor);

    const lineKey = Object.keys(LINE_COLORS).find(
        key => LINE_COLORS[key] === lineColor
    ) || 'default';
    drawer.setAttribute('data-line', lineKey);

    featuresContainer.innerHTML = generateStationFeaturesContent(station);
    content.innerHTML = generateTooltipContent(station);

    overlay.style.display = "block";
    drawer.classList.add("open");

    setTimeout(() => {
        if (
            window.nextTrainModule &&
            typeof window.nextTrainModule.initializeNextTrainForStation === "function"
        ) {
            window.nextTrainModule.initializeNextTrainForStation(station, svgId);
        }
    }, 100);
}

function hideStationDrawer() {
    const drawer = document.getElementById("stationDrawer");
    const overlay = document.getElementById("drawerOverlay");

    drawer.classList.remove("open");
    overlay.style.display = "none";

    if (
        window.nextTrainModule &&
        typeof window.nextTrainModule.stopAllAutoRefresh === "function"
    ) {
        window.nextTrainModule.stopAllAutoRefresh();
    }
}

function generateStationUrl(stationName) {
    const stationSlug = stationName
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w-]/g, '');

    let url = `https://www.ttc.ca/subway-stations/${stationSlug}-station`;

    if (stationSlug === 'york-university') {
        url = `https://www.ttc.ca/subway-stations/${stationSlug}`;
    }

    return url;
}

// ─── Tooltip / drawer content ─────────────────────────────────────────────────

function generateTooltipContent(station) {
    let content = "";

    currentStationContext = station;
    currentStationLine = null;

    for (const lineName of Object.keys(ROUTE_KEYS)) {
        const line = window[lineName];
        if (!line?.stations) continue;

        if (line.stations.includes(station)) {
            currentStationLine = lineName;
            break;
        }
    }

    let isLine2Station = false;
    let line2Alerts = [];

    if (window.line2 && window.line2.stations) {
        for (let i = 0; i < window.line2.stations.length; i++) {
            if (window.line2.stations[i] === station) {
                isLine2Station = true;
                break;
            }
        }

        if (isLine2Station) {
            if (window.alertData && window.alertData.line2 && window.alertData.line2.alerts && window.alertData.line2.alerts.line && Array.isArray(window.alertData.line2.alerts.line)) {
                line2Alerts = window.alertData.line2.alerts.line.filter(alert => {
                    if (!alert.stopNameList || !Array.isArray(alert.stopNameList) || alert.stopNameList.length === 0) {
                        return true;
                    }
                    const affectedStations = alert.stopNameList.filter(stationName => stationName && stationName.trim());
                    return affectedStations.includes(station.name);
                });
            }
        }
    }

    if (isLine2Station && line2Alerts.length > 0) {
        const hasNoServiceAlert = line2Alerts.some(alert => alert.type === "NO_SERVICE");
        const noServiceAlerts = line2Alerts.filter(alert => alert.type === "NO_SERVICE");
        const reducedServiceAlerts = line2Alerts.filter(alert => alert.type === "REDUCED_SERVICE");
        const otherAlerts = line2Alerts.filter(alert => alert.type !== "NO_SERVICE" && alert.type !== "REDUCED_SERVICE");

        const renderAlert = (alert) => {
            let alertColor = "gray";
            let alertIcon = "info";

            if (alert.severity === "Critical" || alert.severity === "CRITICAL") {
                alertColor = "red"; alertIcon = "error";
            } else if (alert.type === "NO_SERVICE") {
                alertColor = "red"; alertIcon = "block";
            } else if (alert.type === "REDUCED_SERVICE") {
                alertColor = "yellow"; alertIcon = "warning";
            } else if (alert.type === "CONSTRUCTION") {
                alertColor = "blue"; alertIcon = "construction";
            } else if (alert.type === "ACCESSIBILITY_ISSUE") {
                alertColor = "orange"; alertIcon = "accessible";
            }

            const alertText = alert.text || alert.headerText || "Line service alert";
            let dateInfo = "";
            if (alert.start && alert.start !== "0001-01-01T00:00:00Z") {
                try {
                    const startDate = new Date(alert.start).toLocaleDateString();
                    if (alert.end && alert.end !== "0001-01-01T00:00:00Z") {
                        dateInfo = `${startDate} - ${new Date(alert.end).toLocaleDateString()}`;
                    } else {
                        dateInfo = `Since: ${startDate}`;
                    }
                } catch (e) {
                    console.warn("Invalid date format:", alert.start, alert.end);
                }
            }

            const getAlertTypeDisplay = (type) => {
                switch (type) {
                    case "NO_SERVICE": return "No Service";
                    case "REDUCED_SERVICE": return "Reduced Service";
                    case "CONSTRUCTION": return "Construction";
                    case "ACCESSIBILITY_ISSUE": return "Accessibility Issue";
                    default: return "Service Alert";
                }
            };

            return `<div class="mb-3 p-3 bg-white dark:bg-gray-800 rounded-lg border-l-4" style="border-left-color: #00B04F;">
                <div class="flex items-start gap-3">
                    <span class="text-${alertColor}-600 dark:text-${alertColor}-400 text-xl">${iconFont(alertIcon, 'text-xl')}</span>
                    <div class="flex-1">
                        <div class="flex items-center gap-2 mb-2">
                            <span class="text-gray-900 dark:text-gray-100 font-medium">Line 2 - Bloor-Danforth</span>
                            <span class="text-xs px-2 py-1 rounded-full" style="background-color: #00B04F; color: white;">${getAlertTypeDisplay(alert.type)}</span>
                        </div>
                        <div class="text-gray-700 dark:text-gray-300 text-sm mb-2">${alertText}</div>
                        ${dateInfo ? `<div class="text-gray-600 dark:text-gray-400 text-xs mb-1">${dateInfo}</div>` : ""}
                        ${alert.url && alert.url.trim() ? `
                            <a href="${alert.url}" target="_blank" class="text-blue-600 text-xs mt-2 hover:underline inline-flex items-center gap-1">
                                View Details
                                ${iconFont('open_in_new', 'text-xs')}
                            </a>
                        ` : ""}
                    </div>
                </div>
            </div>`;
        };

        if (hasNoServiceAlert) {
            noServiceAlerts.forEach(alert => { content += renderAlert(alert); });
            otherAlerts.forEach(alert => { content += renderAlert(alert); });
        } else {
            content += buildNextTrainWidget(station);
            reducedServiceAlerts.forEach(alert => { content += renderAlert(alert); });
            otherAlerts.forEach(alert => { content += renderAlert(alert); });
        }
    } else {
        let stationLineData = null;
        let stationLineAlerts = [];
        let stationLineName = "";
        let stationLineColor = LINE_COLORS.default;

        if (window.line1University && window.line1University.stations) {
            for (let i = 0; i < window.line1University.stations.length; i++) {
                if (window.line1University.stations[i] === station) {
                    stationLineData = window.line1University;
                    stationLineName = "Line 1 - Yonge-University";
                    stationLineColor = LINE_COLORS.line1;
                    break;
                }
            }
        }

        if (!stationLineData && window.line1Yonge && window.line1Yonge.stations) {
            for (let i = 0; i < window.line1Yonge.stations.length; i++) {
                if (window.line1Yonge.stations[i] === station) {
                    stationLineData = window.line1Yonge;
                    stationLineName = "Line 1 - Yonge-University";
                    stationLineColor = LINE_COLORS.line1;
                    break;
                }
            }
        }

        if (!stationLineData && window.line2 && window.line2.stations) {
            for (let i = 0; i < window.line2.stations.length; i++) {
                if (window.line2.stations[i] === station) {
                    stationLineData = window.line2;
                    stationLineName = "Line 2 - Bloor-Danforth";
                    stationLineColor = LINE_COLORS.line2;
                    break;
                }
            }
        }

        if (!stationLineData && window.line4 && window.line4.stations) {
            for (let i = 0; i < window.line4.stations.length; i++) {
                if (window.line4.stations[i] === station) {
                    stationLineData = window.line4;
                    stationLineName = "Line 4 - Sheppard";
                    stationLineColor = LINE_COLORS.line4;
                    break;
                }
            }
        }

        if (!stationLineData && window.line5 && window.line5.stations) {
            for (let i = 0; i < window.line5.stations.length; i++) {
                if (window.line5.stations[i] === station) {
                    stationLineData = window.line5;
                    stationLineName = "Line 5 - Eglinton";
                    stationLineColor = LINE_COLORS.line5;
                    break;
                }
            }
        }

        if (!stationLineData && window.line6 && window.line6.stations) {
            for (let i = 0; i < window.line6.stations.length; i++) {
                if (window.line6.stations[i] === station) {
                    stationLineData = window.line6;
                    stationLineName = "Line 6 - Finch West";
                    stationLineColor = LINE_COLORS.line6;
                    break;
                }
            }
        }

        let lineKey = "";
        if (stationLineName === "Line 1 - Yonge-University") lineKey = "line1University";
        else if (stationLineName === "Line 2 - Bloor-Danforth") lineKey = "line2";
        else if (stationLineName === "Line 4 - Sheppard") lineKey = "line4";
        else if (stationLineName === "Line 5 - Eglinton") lineKey = "line5";
        else if (stationLineName === "Line 6 - Finch West") lineKey = "line6";

        if (lineKey && window.alertData && window.alertData[lineKey] && window.alertData[lineKey].alerts && window.alertData[lineKey].alerts.line && Array.isArray(window.alertData[lineKey].alerts.line)) {
            stationLineAlerts = window.alertData[lineKey].alerts.line.filter(alert => {
                if (!alert.stopNameList || !Array.isArray(alert.stopNameList) || alert.stopNameList.length === 0) return true;
                const affectedStations = alert.stopNameList.filter(s => s && s.trim());
                return affectedStations.includes(station.name);
            });
        }

        // Also check for station-specific alerts
        if (lineKey && window.alertData && window.alertData[lineKey] && window.alertData[lineKey].alerts && window.alertData[lineKey].alerts[station.name] && Array.isArray(window.alertData[lineKey].alerts[station.name])) {
            const stationSpecificAlerts = window.alertData[lineKey].alerts[station.name];
            stationLineAlerts = stationLineAlerts.concat(stationSpecificAlerts);
        }

        if (stationLineName === "Line 1 - Yonge-University") {
            const allLine1Alerts = [];

            if (window.line1University && window.line1University.alerts && window.line1University.alerts.line) {
                allLine1Alerts.push(...window.line1University.alerts.line);
            }

            if (window.line1Yonge && window.line1Yonge.alerts && window.line1Yonge.alerts.line) {
                window.line1Yonge.alerts.line.forEach(alert => {
                    const isDuplicate = allLine1Alerts.some(e => e.text === alert.text && e.start === alert.start);
                    if (!isDuplicate) allLine1Alerts.push(alert);
                });
            }

            stationLineAlerts = allLine1Alerts.filter(alert => {
                if (!alert.stopNameList || !Array.isArray(alert.stopNameList) || alert.stopNameList.length === 0) return true;
                const affectedStations = alert.stopNameList.filter(s => s && s.trim());
                return affectedStations.includes(station.name);
            });

            // Also check for station-specific alerts on both Line 1 variants
            if (window.line1University && window.line1University.alerts && window.line1University.alerts[station.name] && Array.isArray(window.line1University.alerts[station.name])) {
                stationLineAlerts = stationLineAlerts.concat(window.line1University.alerts[station.name]);
            }
            if (window.line1Yonge && window.line1Yonge.alerts && window.line1Yonge.alerts[station.name] && Array.isArray(window.line1Yonge.alerts[station.name])) {
                window.line1Yonge.alerts[station.name].forEach(alert => {
                    const isDuplicate = stationLineAlerts.some(e => e.text === alert.text && e.start === alert.start);
                    if (!isDuplicate) stationLineAlerts.push(alert);
                });
            }
        }

        if (stationLineAlerts.length > 0) {
            const hasNoServiceAlert = stationLineAlerts.some(alert => alert.type === "NO_SERVICE");
            const noServiceAlerts = stationLineAlerts.filter(alert => alert.type === "NO_SERVICE");
            const reducedServiceAlerts = stationLineAlerts.filter(alert => alert.type === "REDUCED_SERVICE");
            const otherAlerts = stationLineAlerts.filter(alert => alert.type !== "NO_SERVICE" && alert.type !== "REDUCED_SERVICE");

            const renderAlert = (alert) => {
                let alertColor = "gray";
                let alertIcon = "info";

                if (alert.severity === "Critical" || alert.severity === "CRITICAL") {
                    alertColor = "red"; alertIcon = "error";
                } else if (alert.type === "NO_SERVICE") {
                    alertColor = "red"; alertIcon = "block";
                } else if (alert.type === "REDUCED_SERVICE") {
                    alertColor = "yellow"; alertIcon = "warning";
                } else if (alert.type === "CONSTRUCTION") {
                    alertColor = "blue"; alertIcon = "construction";
                } else if (alert.type === "ACCESSIBILITY_ISSUE") {
                    alertColor = "orange"; alertIcon = "accessible";
                }

                const alertText = alert.text || alert.headerText || "Line service alert";
                let dateInfo = "";
                if (alert.start && alert.start !== "0001-01-01T00:00:00Z") {
                    try {
                        const startDate = new Date(alert.start).toLocaleDateString();
                        if (alert.end && alert.end !== "0001-01-01T00:00:00Z") {
                            dateInfo = `${startDate} - ${new Date(alert.end).toLocaleDateString()}`;
                        } else {
                            dateInfo = `Since: ${startDate}`;
                        }
                    } catch (e) {
                        console.warn("Invalid date format:", alert.start, alert.end);
                    }
                }

                const getAlertTypeDisplay = (type) => {
                    switch (type) {
                        case "NO_SERVICE": return "No Service";
                        case "REDUCED_SERVICE": return "Reduced Service";
                        case "CONSTRUCTION": return "Construction";
                        case "ACCESSIBILITY_ISSUE": return "Accessibility Issue";
                        default: return "Service Alert";
                    }
                };

                return `<div class="mb-3 p-3 bg-white dark:bg-gray-800 rounded-lg border-l-4" style="border-left-color: ${stationLineColor};">
                    <div class="flex items-start gap-3">
                        <span class="text-${alertColor}-600 dark:text-${alertColor}-400 text-xl">${iconFont(alertIcon, 'text-xl')}</span>
                        <div class="flex-1">
                            <div class="flex items-center gap-2 mb-2">
                                <span class="text-gray-900 dark:text-gray-100 font-medium">${stationLineName}</span>
                                <span class="text-xs px-2 py-1 rounded-full" style="background-color: ${stationLineColor}; color: white;">${getAlertTypeDisplay(alert.type)}</span>
                            </div>
                            <div class="text-gray-700 dark:text-gray-300 text-sm mb-2">${alertText}</div>
                            ${dateInfo ? `<div class="text-gray-600 dark:text-gray-400 text-xs mb-1">${dateInfo}</div>` : ""}
                            ${alert.url && alert.url.trim() ? `
                                <a href="${alert.url}" target="_blank" class="text-blue-600 text-xs mt-2 hover:underline inline-flex items-center gap-1">
                                    View Details
                                    ${iconFont('open_in_new', 'text-xs')}
                                </a>
                            ` : ""}
                        </div>
                    </div>
                </div>`;
            };

            if (hasNoServiceAlert) {
                noServiceAlerts.forEach(alert => { content += renderAlert(alert); });
                otherAlerts.forEach(alert => { content += renderAlert(alert); });
            } else {
                content += buildNextTrainWidget(station);
                reducedServiceAlerts.forEach(alert => { content += renderAlert(alert); });
                otherAlerts.forEach(alert => { content += renderAlert(alert); });
            }
        } else {
            content += buildNextTrainWidget(station);
        }
    }

    // Connections
    if (station.connections.length > 0) {
        content += `<div class="mb-3 p-3 bg-gray-50 rounded-lg">
      <div class="flex items-center gap-3 mb-2">
        ${iconFont('transfer_within_a_station', 'text-gray-600 text-xl')}
        <span class="text-gray-700 font-medium">Connects to:</span>
      </div>`;

        function getLineAlertsForConnection(connectionLineName, stationName) {
            let alerts = [];

            if (["Line 1", "Line 1 University", "Line 1 Yonge"].includes(connectionLineName)) {
                const allLine1Alerts = [];
                if (window.line1University && window.line1University.alerts && window.line1University.alerts.line) {
                    allLine1Alerts.push(...window.line1University.alerts.line);
                }
                if (window.line1Yonge && window.line1Yonge.alerts && window.line1Yonge.alerts.line) {
                    window.line1Yonge.alerts.line.forEach(alert => {
                        const isDuplicate = allLine1Alerts.some(e => e.text === alert.text && e.start === alert.start);
                        if (!isDuplicate) allLine1Alerts.push(alert);
                    });
                }
                alerts = allLine1Alerts;
            } else if (connectionLineName === "Line 2") {
                if (window.line2 && window.line2.alerts && window.line2.alerts.line) alerts = window.line2.alerts.line;
            } else if (connectionLineName === "Line 4") {
                if (window.line4 && window.line4.alerts && window.line4.alerts.line) alerts = window.line4.alerts.line;
            } else if (connectionLineName === "Line 5") {
                if (window.line5 && window.line5.alerts && window.line5.alerts.line) alerts = window.line5.alerts.line;
            } else if (connectionLineName === "Line 6") {
                if (window.line6 && window.line6.alerts && window.line6.alerts.line) alerts = window.line6.alerts.line;
            }

            const relevantAlerts = alerts.filter(alert => {
                if (!alert.stopNameList || !Array.isArray(alert.stopNameList) || alert.stopNameList.length === 0) return true;
                const affectedStations = alert.stopNameList.filter(s => s && s.trim());
                return affectedStations.includes(stationName);
            });

            return { alerts: relevantAlerts };
        }

        const stationOwnLine = getStationOwnLine(station);
        const allLinesToShow = [];
        station.connections.forEach(connection => {
            if (!allLinesToShow.includes(connection)) allLinesToShow.push(connection);
        });

        console.log(`>>>> 🔍 ${station.name} - Own line: ${stationOwnLine}, Connections: ${station.connections.join(', ')}, All lines to show: ${allLinesToShow.join(', ')}`);

        const connectionSpans = allLinesToShow.map((lineName) => {
            const lineColor = getLineColor(lineName);
            const connectionId = `connection-${station.name.replace(/\s+/g, "-").toLowerCase()}-${lineName.replace(/\s+/g, "-").toLowerCase()}`;

            let connectionText = `<div id="${connectionId}" class="flex-1 min-w-0 mb-2 p-2 bg-white rounded border">
        <span class="text-gray-700 connection-span font-medium" style="border-bottom-color: ${lineColor};">${lineName}</span>`;

            if (
                (lineName === "Line 1 University" || lineName === "Line 1 Yonge") &&
                station.name === "Union"
            ) {
                connectionText += ` <span class="text-green-600 text-xs font-medium">(Remain on train)</span>`;
            }

            const { alerts: connectionAlerts } = getLineAlertsForConnection(lineName, station.name);

            if (connectionAlerts.length > 0) {
                connectionText += `<div class="mt-2 space-y-1">`;
                connectionAlerts.forEach((alert) => {
                    let alertColor = "gray";
                    let alertIcon = "info";

                    if (alert.severity === "Critical" || alert.severity === "CRITICAL") {
                        alertColor = "red"; alertIcon = "error";
                    } else if (alert.type === "NO_SERVICE") {
                        alertColor = "red"; alertIcon = "block";
                    } else if (alert.type === "REDUCED_SERVICE") {
                        alertColor = "yellow"; alertIcon = "warning";
                    } else if (alert.type === "CONSTRUCTION") {
                        alertColor = "blue"; alertIcon = "construction";
                    } else if (alert.type === "ACCESSIBILITY_ISSUE") {
                        alertColor = "orange"; alertIcon = "accessible";
                    }

                    const alertText = alert.text || alert.headerText || "Line service alert";
                    let dateInfo = "";
                    if (alert.start && alert.start !== "0001-01-01T00:00:00Z") {
                        try {
                            const startDate = new Date(alert.start).toLocaleDateString();
                            if (alert.end && alert.end !== "0001-01-01T00:00:00Z") {
                                dateInfo = `${startDate} - ${new Date(alert.end).toLocaleDateString()}`;
                            } else {
                                dateInfo = `Since: ${startDate}`;
                            }
                        } catch (e) {
                            console.warn("Invalid date format:", alert.start, alert.end);
                        }
                    }

                    connectionText += `<div class="flex items-start gap-1 p-1 bg-${alertColor}-50 rounded text-xs border-l-2 border-${alertColor}-300">
            <span class="text-${alertColor}-600" style="font-size: 14px;">${iconFont(alertIcon, '')}</span>
            <div class="flex-1 min-w-0">
              <div class="text-${alertColor}-700 font-medium">${alert.type === "NO_SERVICE" ? "No Service" : alert.type === "REDUCED_SERVICE" ? "Reduced Service" : alert.type === "CONSTRUCTION" ? "Construction" : "Alert"}</div>
              <div class="text-${alertColor}-600 text-xs leading-tight">${alertText}</div>
              ${dateInfo ? `<div class="text-${alertColor}-500 text-xs mt-1">${dateInfo}</div>` : ""}
            </div>
          </div>`;
                });
                connectionText += `</div>`;
            }

            connectionText += `<div class="connection-train-times mt-1 w-full"></div>`;
            connectionText += `</div>`;

            return connectionText;
        });

        content += `<div class="flex flex-col gap-2 w-full">${connectionSpans.join("")}</div>`;
        content += `</div></div>`;
    }

    const accessibilityAndConstructionContent = generateAccessibilityContent(station);
    const stationAlertsContent = generateStationAlerts(station);
    const hasNotices = accessibilityAndConstructionContent.trim() || stationAlertsContent.trim();

    if (hasNotices) {
        content += `<div class="mb-3 p-3 bg-gray-50 rounded-lg">`;
        content += `<div class="flex items-center gap-3 mb-2">`;
        content += `${iconFont('info', 'text-blue-600 text-xl')}<span class="text-blue-700 font-medium">Station Notices</span>`;
        content += `</div>`;
        content += accessibilityAndConstructionContent + stationAlertsContent;
        content += `</div>`;
    }

    window.currentStationContext = currentStationContext;
    window.currentStationLine = currentStationLine;

    return content;
}

/**
 * Build the "Next Trains" widget HTML for a station.
 * Extracted to avoid repeating the same block three times.
 *
 * @param {Object} station - Station object
 * @returns {string} - HTML string
 */
function buildNextTrainWidget(station) {
    const stationSlug = station.name.replace(/\s+/g, "-").toLowerCase();
    return `<div id="next-train-container-${stationSlug}" class="mb-3 p-3 bg-blue-50 rounded-lg">
        <div class="flex items-center justify-between mb-2">
          <div class="flex items-center gap-3">
            ${iconFont('schedule', 'text-blue-600 text-xl')}
            <span class="text-blue-700 font-medium">Next Trains</span>
          </div>
          <button
            id="refresh-train-times-${stationSlug}"
            onclick="event.stopPropagation(); refreshTrainTimes(currentStationContext)"
            class="flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh train times">
            ${iconFont('refresh', 'text-sm')}
            <span>Refresh</span>
          </button>
        </div>
        <div class="next-train-times text-blue-600">Loading...</div>
      </div>`;
}

// ─── Station features ─────────────────────────────────────────────────────────

function generateStationFeaturesContent(station) {
    let content = "";

    if (!station.features || !Array.isArray(station.features) || station.features.length === 0) {
        return content;
    }

    let filteredFeatures = station.features.filter(feature =>
        feature.toLowerCase() !== 'accessible'
    );

    const prestoFeatures = filteredFeatures.filter(f => f.toLowerCase().includes('presto'));
    const otherFeatures = filteredFeatures.filter(f => !f.toLowerCase().includes('presto'));
    filteredFeatures = [...otherFeatures, ...prestoFeatures];

    const hasBikeShare = station.nearest_bikeshare && station.nearest_bikeshare.length > 0;
    const totalFeaturesCount = filteredFeatures.length + (hasBikeShare ? 1 : 0);

    if (totalFeaturesCount === 0) return content;

    const sectionId = `station-features-${station.name.replace(/\s+/g, "-").replace(/['"]/g, "").toLowerCase()}`;

    content += `<div>
        <button
            onclick="toggleStationFeatures('${sectionId}')"
            class="flex items-center justify-between w-full text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 rounded p-3 bg-gray-50"
            aria-expanded="false"
            aria-controls="${sectionId}-content">
            <div class="flex items-center gap-3">
                ${iconFont('info', 'text-blue-600 text-xl')}
                <span class="text-blue-700 font-medium">Station Features</span>
                <span class="text-xs px-2 py-1 bg-blue-200 text-blue-800 rounded-full">${totalFeaturesCount}</span>
            </div>
            <span id="${sectionId}-chevron" class="transition-transform duration-200">${iconFont('chevron-down', 'text-blue-600')}</span>
        </button>

        <div id="${sectionId}-content" class="hidden p-3 bg-gray-50">
            <div class="grid grid-cols-1 gap-2">`;

    const getFeatureIcon = (feature) => {
        const featureLower = feature.toLowerCase();

        if (featureLower.includes('bike') && featureLower.includes('share')) {
            return { type: 'image', html: `<img src="/img/BikeShare_logo.png" alt="Bike Share" class="w-5 h-5">` };
        }
        if (featureLower.includes('presto')) {
            return { type: 'image', html: `<img src="/img/presto.png" alt="Presto" class="w-4 h-4">` };
        }
        if (featureLower.includes('bicycle') && featureLower.includes('repair')) {
            return {
                type: 'stacked', html: `<span class="fa-stack fa-sm">
                    <i class="fa-solid fa-bicycle fa-stack-1x text-red-600"></i>
                    <i class="fa-solid fa-wrench fa-stack-1x" style="transform: translate(-6px, -6px); font-size: 0.6em; color: #888;"></i>
                </span>`
            };
        }
        if (featureLower.includes('bicycle') && featureLower.includes('lock')) {
            return {
                type: 'stacked', html: `<span class="fa-stack fa-sm">
                    <i class="fa-solid fa-bicycle fa-stack-1x text-red-600"></i>
                    <i class="fa-solid fa-lock fa-stack-1x" style="transform: translate(-6px, -6px); font-size: 0.6em; color: #444;"></i>
                </span>`
            };
        }
        if (featureLower.includes('parking')) {
            return {
                type: 'stacked', html: `<span class="fa-stack fa-sm">
                    <i class="fa-regular fa-circle fa-stack-2x text-green-600"></i>
                    <i class="fa-solid fa-p fa-stack-1x text-green-600" style="transform: translateX(5px);"></i>
                </span>`
            };
        }
        if (featureLower.includes('pick-up') || featureLower.includes('drop-off')) {
            return {
                type: 'stacked', html: `<span class="relative inline-flex items-end gap-1" style="padding-top: 8px; padding-left: 2px;">
                    <i class="fa-solid fa-person text-sm"></i>
                    <i class="fa-solid fa-car text-sm"></i>
                    <i class="fa-solid fa-exchange-alt text-xs absolute" style="top: 0; left: 50%; transform: translateX(calc(-50% + 2px));"></i>
                </span>`
            };
        }
        if (featureLower.includes('cellular')) {
            return {
                type: 'stacked', html: `<span class="relative inline-block">
                    <i class="fa-solid fa-mobile-retro text-sm text-black" style="position: relative; top: -2px; left: -4px;"></i>
                    <i class="fa-solid fa-signal text-xs text-blue-600 absolute" style="bottom: -2px; left: 50%; transform: translateX(-50%);"></i>
                </span>`
            };
        }
        if (featureLower.includes('designated') && featureLower.includes('waiting')) {
            return { type: 'image', html: `<i class="fa-solid fa-person-arrow-down-to-line text-xs absolute"></i>` };
        }
        if (featureLower.includes('accessible')) return { type: 'regular', icon: 'accessible' };
        if (featureLower.includes('bicycle') || featureLower.includes('bike')) return { type: 'regular', icon: 'directions_bike' };
        if (featureLower.includes('washroom')) return { type: 'regular', icon: 'wc' };
        if (featureLower.includes('waiting')) return { type: 'regular', icon: 'event_seat' };
        if (featureLower.includes('repair')) return { type: 'regular', icon: 'build' };
        if (featureLower.includes('share')) return { type: 'regular', icon: 'share' };

        return { type: 'regular', icon: 'check_circle' };
    };

    filteredFeatures.forEach(feature => {
        const iconData = getFeatureIcon(feature);
        const featureLower = feature.toLowerCase();
        let iconHtml;

        if (iconData.type === 'stacked' || iconData.type === 'image') {
            iconHtml = iconData.html;
        } else {
            iconHtml = iconFont(iconData.icon, 'text-sm');
        }

        if (featureLower.includes('presto')) {
            content += `
                <div class="flex items-center justify-between bg-white rounded border" style="padding: 0.5rem; min-height: 2.5rem;">
                    <img src="/img/presto.png" alt="Presto" class="h-6" style="max-width: 80px;">
                    <a href="https://www.prestocard.ca/" target="_blank" class="ml-2 text-gray-500 hover:text-blue-600 transition-colors" title="Visit PRESTO website">
                        <i class="fa-solid fa-link"></i>
                    </a>
                </div>`;
        } else if (featureLower.includes('bike') && featureLower.includes('share')) {
            content += `
                <div class="flex items-center justify-between bg-white rounded border" style="padding: 0.5rem; min-height: 2.5rem;">
                    <div class="flex items-center gap-2">
                        <img src="/img/BikeShare_logo.png" alt="Bike Share" class="w-5 h-5">
                        <span class="text-gray-700 text-sm">${feature}</span>
                    </div>
                    <a href="https://bikesharetoronto.com/" target="_blank" class="ml-2 text-gray-500 hover:text-blue-600 transition-colors" title="Visit Bike Share Toronto website">
                        <i class="fa-solid fa-link"></i>
                    </a>
                </div>`;
        } else if (iconData.type === 'stacked') {
            content += `
                <div class="flex items-center gap-2 bg-white rounded border" style="padding: 0.5rem; min-height: 2.5rem;">
                    <div class="flex items-center justify-center" style="width: 1.25rem; height: 1.25rem;">${iconHtml}</div>
                    <span class="text-gray-700 text-sm">${feature}</span>
                </div>`;
        } else {
            content += `
                <div class="flex items-center gap-2 bg-white rounded border" style="padding: 0.5rem; min-height: 2.5rem;">
                    <div class="flex items-center justify-center" style="width: 1.25rem; height: 1.25rem;">${iconHtml}</div>
                    <span class="text-gray-700 text-sm">${feature}</span>
                </div>`;
        }
    });

    // ── Bike share nearby stations ─────────────────────────────────────────────
    if (hasBikeShare) {
        content += `
            <div class="flex items-center justify-between bg-white rounded border" style="padding: 0.5rem; min-height: 2.5rem;">
                <div class="flex items-center gap-2">
                    <img src="/img/BikeShare_logo.png" alt="Bike Share" class="w-5 h-5">
                    <span class="text-gray-700 text-sm">Nearby Bike Share (within 300m)<br/>3-5 minute walk</span>
                </div>
                <a href="https://bikesharetoronto.com/" target="_blank" class="ml-2 text-gray-500 hover:text-blue-600 transition-colors" title="Visit Bike Share Toronto website">
                    <i class="fa-solid fa-link"></i>
                </a>
            </div>`;

        // Render the 2 closest stations immediately with a "checking…" placeholder,
        // then kick off the async live-availability enrichment
        const initialStations = station.nearest_bikeshare.slice(0, 2);
        content += `<div id="bike-stations-container">`;
        initialStations.forEach((bikeStation, index) => {
            const googleMapsUrl = `https://www.google.com/maps?q=${bikeStation.lat},${bikeStation.lon}`;
            const bikeStationId = `bike-station-${bikeStation.station_id || index}`;

            content += `${renderBikeStationCard(bikeStation)}`;
        });
        content += `</div>`;

        // Fire async enrichment after the HTML is in the DOM
        if (window.BikeShareAPI || true) {
            // Small defer so the rendered HTML is flushed before we start patching it
            setTimeout(() => enrichBikeShareAvailability(station, initialStations), 0);
        }
    }

    content += `
            </div>
        </div>
    </div>`;

    return content;
}

function toggleStationFeatures(sectionId) {
    const content = document.getElementById(`${sectionId}-content`);
    const chevron = document.getElementById(`${sectionId}-chevron`);
    const button = content.previousElementSibling;

    if (content.classList.contains('hidden')) {
        content.classList.remove('hidden');
        chevron.style.transform = 'rotate(180deg)';
        button.setAttribute('aria-expanded', 'true');
    } else {
        content.classList.add('hidden');
        chevron.style.transform = 'rotate(0deg)';
        button.setAttribute('aria-expanded', 'false');
    }
}

// ─── Accessibility content ────────────────────────────────────────────────────

function generateAccessibilityContent(station) {
    let content = "";
    let accessibilityAlerts = [];
    let constructionAlerts = [];

    const branches = ['line1University', 'line1Yonge', 'line2', 'line4', 'line5', 'line6'];

    for (let i = 0; i < branches.length; i++) {
        const branch = branches[i];
        const branchData = window[branch];
        const alertsForBranch = window.alertData?.[branch]?.alerts || window.alertData?.[branch];

        if (branchData?.stations && alertsForBranch?.[station.name]) {
            const foundStation = branchData.stations.find(s => s.name === station.name);
            if (foundStation) {
                accessibilityAlerts.push(
                    ...alertsForBranch[station.name].filter(a => a.type === "ACCESSIBILITY_ISSUE")
                );
                constructionAlerts.push(
                    ...alertsForBranch[station.name].filter(a => a.type === "CONSTRUCTION")
                );
            }
        }
    }

    console.log(`🔍 Station: ${station.name}, Accessibility alerts: ${accessibilityAlerts.length}, Construction alerts: ${constructionAlerts.length}`);

    accessibilityAlerts.forEach((alert) => {
        const isNewFormat = alert.hasOwnProperty('site_wide') && alert.hasOwnProperty('type');
        let text, equipmentType, equipmentIcon, startDate, url, severity;

        if (isNewFormat) {
            text = alert.text || alert.headerText || "Equipment out of service";
            severity = alert.severity || "";
            url = alert.url || "";
            startDate = "";
            if (alert.start && alert.start !== "0001-01-01T00:00:00Z") {
                try { startDate = new Date(alert.start).toLocaleDateString(); } catch (e) { }
            }
        } else {
            text = alert.Title || "Equipment out of service";
            severity = alert.severity || "";
            url = alert.URL || "";
            startDate = alert.EffectiveDateTitle || "";
        }

        equipmentType = "equipment";
        equipmentIcon = "warning";
        if (text.toLowerCase().includes("elevator")) { equipmentType = "elevator"; equipmentIcon = "elevator"; }
        else if (text.toLowerCase().includes("escalator")) { equipmentType = "escalator"; equipmentIcon = "escalator"; }
        else if (text.toLowerCase().includes("washroom")) { equipmentType = "washroom"; equipmentIcon = "wc"; }

        let alertColor = "orange";
        if (severity === "Critical" || severity === "CRITICAL") alertColor = "red";
        else if (severity === "WARNING" || severity === "Warning") alertColor = "yellow";

        content += `<div class="flex items-start gap-2 mt-2 p-2 bg-${alertColor}-50 rounded border-l-4 border-${alertColor}-400">
        <span class="text-${alertColor}-600 text-lg">${iconFont(equipmentIcon, 'text-lg')}</span>
        <div class="flex-1">
          <div class="text-${alertColor}-700 font-medium text-sm">
            ${equipmentType.charAt(0).toUpperCase() + equipmentType.slice(1)} Alert
            ${severity ? `<span class="ml-2 text-xs px-2 py-1 bg-${alertColor}-200 text-${alertColor}-800 rounded-full">${severity}</span>` : ""}
          </div>
          <div class="text-${alertColor}-600 text-xs mt-1">${text}</div>
          ${startDate ? `<div class="text-${alertColor}-500 text-xs mt-1">${isNewFormat ? 'Since:' : ''} ${startDate}</div>` : ""}
          ${url ? `<a href="${url}" target="_blank" class="text-blue-600 text-xs mt-1 hover:underline inline-flex items-center gap-1">View Details ${iconFont('open_in_new', 'text-xs')}</a>` : ""}
        </div>
      </div>`;
    });

    constructionAlerts.forEach((alert) => {
        const text = alert.text || alert.headerText || "Construction notice";
        const url = alert.url || "";
        let dateInfo = "";
        if (alert.start && alert.start !== "0001-01-01T00:00:00Z") {
            try {
                const startDate = new Date(alert.start).toLocaleDateString();
                if (alert.end && alert.end !== "0001-01-01T00:00:00Z") {
                    dateInfo = `${startDate} - ${new Date(alert.end).toLocaleDateString()}`;
                } else {
                    dateInfo = `Since: ${startDate}`;
                }
            } catch (e) { }
        }

        content += `<div class="flex items-start gap-2 mt-2 p-2 bg-blue-50 rounded border-l-4 border-blue-400">
        ${iconFont('construction', 'text-blue-700 text-lg')}
        <div class="flex-1">
          <div class="text-blue-700 font-medium text-sm">Construction Notice</div>
          <div class="text-blue-600 text-xs mt-1">${text}</div>
          ${dateInfo ? `<div class="text-blue-500 text-xs mt-1">${dateInfo}</div>` : ""}
          ${url ? `<a href="${url}" target="_blank" class="text-blue-600 text-xs mt-1 hover:underline inline-flex items-center gap-1">View Details ${iconFont('open_in_new', 'text-xs')}</a>` : ""}
        </div>
      </div>`;
    });

    return content;
}

function toggleLineAlerts(sectionId) {
    const content = document.getElementById(`${sectionId}-content`);
    const chevron = document.getElementById(`${sectionId}-chevron`);
    const button = content.previousElementSibling;

    if (content.classList.contains('hidden')) {
        content.classList.remove('hidden');
        chevron.style.transform = 'rotate(180deg)';
        button.setAttribute('aria-expanded', 'true');
    } else {
        content.classList.add('hidden');
        chevron.style.transform = 'rotate(0deg)';
        button.setAttribute('aria-expanded', 'false');
    }
}

// ─── Station alerts ───────────────────────────────────────────────────────────

function generateStationAlerts(station) {
    let content = "";

    if (station.alerts && Array.isArray(station.alerts) && station.alerts.length > 0) {
        station.alerts.forEach((alert) => {
            let alertColor = "gray";
            let alertIcon = "info";

            if (alert.severity === "Critical") { alertColor = "red"; alertIcon = "error"; }
            else if (alert.type === "ACCESSIBILITY_ISSUE") { alertColor = "orange"; alertIcon = "accessible"; }
            else if (alert.type === "NO_SERVICE" || alert.type === "REDUCED_SERVICE") { alertColor = "yellow"; alertIcon = "warning"; }

            const alertText = alert.headerText || alert.text || "Service alert";
            const alertTitle =
                alert.type === "ACCESSIBILITY_ISSUE" ? "Accessibility Alert" :
                    alert.type === "NO_SERVICE" ? "No Service" :
                        alert.type === "REDUCED_SERVICE" ? "Reduced Service" : "Station Alert";

            content += `<div class="flex items-start gap-3 mb-3 p-3 bg-${alertColor}-50 rounded-lg border-l-4 border-${alertColor}-400">
        <span class="text-${alertColor}-600 text-xl">${iconFont(alertIcon, 'text-xl')}</span>
        <div class="flex-1">
          <div class="flex items-center justify-between mb-1">
            <span class="text-${alertColor}-700 font-medium">${alertTitle}</span>
            ${alert.severity ? `<span class="text-xs px-2 py-1 bg-${alertColor}-200 text-${alertColor}-800 rounded-full">${alert.severity}</span>` : ""}
          </div>
          <span class="text-${alertColor}-600 text-sm">${alertText}</span>
          ${alert.start && alert.start !== "0001-01-01T00:00:00Z"
                    ? `<div class="text-xs text-${alertColor}-500 mt-1">Since: ${new Date(alert.start).toLocaleDateString()}</div>`
                    : ""}
        </div>
      </div>`;
        });
    }

    if (station.lineAlert && station.lineAlert.trim()) {
        const alertColor = station.alertSeverity === "critical" ? "red" : station.alertSeverity === "warning" ? "yellow" : "gray";
        const alertIcon = station.alertSeverity === "critical" ? "error" : "warning";
        content += `<div class="flex items-start gap-3 mb-3 p-3 bg-${alertColor}-50 rounded-lg"><span class="text-${alertColor}-600 text-xl">${iconFont(alertIcon, 'text-xl')}</span><div><span class="text-${alertColor}-700 font-medium">Line Alert:</span><br><span class="text-${alertColor}-600">${station.lineAlert}</span></div></div>`;
    }

    if (station.constructionNotice && station.constructionNotice.trim()) {
        content += `<div class="flex items-start gap-3 mb-3 p-3 bg-blue-50 rounded-lg">${iconFont('construction', 'text-blue-700 text-xl')}<div><span class="text-blue-700 font-medium">Construction Notice:</span><br><span class="text-blue-600">${station.constructionNotice}</span></div></div>`;
    }

    if (station.elevatorOut) {
        if (station.elevatorAlert && Array.isArray(station.elevatorAlert) && station.elevatorAlert.length > 0) {
            station.elevatorAlert.forEach((alert, index) => {
                if (alert && alert.trim()) {
                    const alertNumber = station.elevatorAlert.length > 1 ? ` (${index + 1})` : "";
                    content += `<div class="flex items-start gap-3 mb-3 p-3 bg-red-50 rounded-lg">${iconFont('elevator', 'text-red-600 text-xl')}<div><span class="text-red-700 font-medium">Elevator Alert${alertNumber}:</span><br><span class="text-red-600">${alert}</span></div></div>`;
                }
            });
        } else if (station.elevatorAlert && typeof station.elevatorAlert === "string" && station.elevatorAlert.trim()) {
            content += `<div class="flex items-start gap-3 mb-3 p-3 bg-red-50 rounded-lg">${iconFont('elevator', 'text-red-600 text-xl')}<div><span class="text-red-700 font-medium">Elevator Alert:</span><br><span class="text-red-600">${station.elevatorAlert}</span></div></div>`;
        } else {
            content += `<div class="flex items-center gap-3 mb-3 p-3 bg-red-50 rounded-lg">${iconFont('elevator', 'text-red-600 text-xl')}<span class="text-red-700 font-medium">Elevator Out of Service</span></div>`;
        }
    }

    return content;
}

// ─── Global line alerts (home page) ──────────────────────────────────────────

function generateGlobalLineAlerts() {
    const container = document.getElementById('globalAlertsContainer');
    if (!container) return;

    const allLineAlerts = [];

    const addAlertsFromLine = (lineData, lineName) => {
        if (lineData && lineData.alerts && lineData.alerts.line && Array.isArray(lineData.alerts.line)) {
            lineData.alerts.line.forEach(alert => {
                const isDuplicate = allLineAlerts.some(e =>
                    e.text === alert.text && e.start === alert.start && e.lineName === lineName
                );
                if (!isDuplicate) {
                    allLineAlerts.push({ ...alert, lineName, lineColor: getLineColor(lineName) });
                }
            });
        }
    };

    for (const [lineKey, label] of Object.entries(ROUTE_KEYS)) {
        const line = window[lineKey];
        if (!line) continue;
        addAlertsFromLine(line, label);
    }

    const uniqueAlerts = [];
    allLineAlerts.forEach(alert => {
        const isDuplicate = uniqueAlerts.some(e => e.text === alert.text && e.start === alert.start);
        if (!isDuplicate) uniqueAlerts.push(alert);
    });

    console.log(`🚨 Found ${uniqueAlerts.length} global line alerts`);

    if (uniqueAlerts.length === 0) {
        container.classList.add('hidden');
        return;
    }

    container.classList.remove('hidden');

    let content = `
        <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <button
                onclick="toggleGlobalAlerts()"
                class="flex items-center justify-between w-full text-left focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 rounded"
                aria-expanded="false"
                aria-controls="global-alerts-content">
                <div class="flex items-center gap-3">
                    ${iconFont('warning', 'text-red-600 dark:text-red-400 text-xl')}
                    <span class="font-medium text-lg text-gray-900 dark:text-gray-100">Line Service Alerts</span>
                    <span class="text-xs px-2 py-1 bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-200 rounded-full">${uniqueAlerts.length}</span>
                </div>
                <span id="global-alerts-chevron" class="transition-transform duration-200">${iconFont('chevron-down', 'text-gray-600 dark:text-gray-400')}</span>
            </button>

            <div id="global-alerts-content" class="hidden mt-4 space-y-3">
    `;

    uniqueAlerts.forEach((alert) => {
        let alertColor = "gray";
        let alertIcon = "info";

        if (alert.severity === "Critical" || alert.severity === "CRITICAL") { alertColor = "red"; alertIcon = "error"; }
        else if (alert.type === "NO_SERVICE") { alertColor = "red"; alertIcon = "block"; }
        else if (alert.type === "REDUCED_SERVICE") { alertColor = "yellow"; alertIcon = "warning"; }
        else if (alert.type === "CONSTRUCTION") { alertColor = "blue"; alertIcon = "construction"; }
        else if (alert.type === "ACCESSIBILITY_ISSUE") { alertColor = "orange"; alertIcon = "accessible"; }

        const alertText = alert.text || alert.headerText || "Line service alert";
        let dateInfo = "";
        if (alert.start && alert.start !== "0001-01-01T00:00:00Z") {
            try {
                const startDate = new Date(alert.start).toLocaleDateString();
                if (alert.end && alert.end !== "0001-01-01T00:00:00Z") {
                    dateInfo = `${startDate} - ${new Date(alert.end).toLocaleDateString()}`;
                } else {
                    dateInfo = `Since: ${startDate}`;
                }
            } catch (e) { }
        }

        let affectedStations = "";
        if (alert.stopNameList && Array.isArray(alert.stopNameList) && alert.stopNameList.length > 0) {
            const validStations = alert.stopNameList.filter(s => s && s.trim());
            if (validStations.length > 0) {
                affectedStations = `<div class="text-xs text-gray-600 dark:text-gray-400 mt-2">Affected stations: ${validStations.join(", ")}</div>`;
            }
        }

        const getAlertTypeDisplay = (type) => {
            switch (type) {
                case "NO_SERVICE": return "No Service";
                case "REDUCED_SERVICE": return "Reduced Service";
                case "CONSTRUCTION": return "Construction";
                case "ACCESSIBILITY_ISSUE": return "Accessibility Issue";
                default: return "Service Alert";
            }
        };

        content += `
            <div class="flex items-start gap-3 p-3 rounded-lg border-l-4 bg-white dark:bg-gray-800" style="border-left-color: ${alert.lineColor};">
                <span class="text-${alertColor}-600 dark:text-${alertColor}-400 text-xl">${iconFont(alertIcon, 'text-xl')}</span>
                <div class="flex-1">
                    <div class="flex items-center justify-between mb-2">
                        <div class="flex items-center gap-2">
                            <span class="text-gray-900 dark:text-gray-100 font-medium">${alert.lineName}</span>
                            <span class="text-xs px-2 py-1 rounded-full" style="background-color: ${alert.lineColor}; color: ${alert.lineColor === '#FFD320' ? '#374151' : 'white'};">${getAlertTypeDisplay(alert.type)}</span>
                            ${alert.severity ? `<span class="text-xs px-2 py-1 bg-${alertColor}-200 dark:bg-${alertColor}-800 text-${alertColor}-800 dark:text-${alertColor}-200 rounded-full">${alert.severity}</span>` : ""}
                        </div>
                    </div>
                    <div class="text-gray-700 dark:text-gray-300 text-sm mb-2">${alertText}</div>
                    ${dateInfo ? `<div class="text-gray-600 dark:text-gray-400 text-xs mb-1">${dateInfo}</div>` : ""}
                    ${affectedStations}
                    ${alert.url && alert.url.trim() ? `
                        <a href="${alert.url}" target="_blank" class="text-blue-600 text-xs mt-2 hover:underline inline-flex items-center gap-1">
                            View Details
                            ${iconFont('open_in_new', 'text-xs')}
                        </a>
                    ` : ""}
                </div>
            </div>
        `;
    });

    content += `</div></div>`;
    container.innerHTML = content;
}

function toggleGlobalAlerts() {
    const content = document.getElementById('global-alerts-content');
    const chevron = document.getElementById('global-alerts-chevron');
    const button = content.previousElementSibling;

    if (content.classList.contains('hidden')) {
        content.classList.remove('hidden');
        chevron.style.transform = 'rotate(180deg)';
        button.setAttribute('aria-expanded', 'true');
    } else {
        content.classList.add('hidden');
        chevron.style.transform = 'rotate(0deg)';
        button.setAttribute('aria-expanded', 'false');
    }
}

// ─── Initialise on DOM ready ──────────────────────────────────────────────────

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeStationUI);
} else {
    initializeStationUI();
}

// ─── Global exports ───────────────────────────────────────────────────────────

window.showStationDrawer = showStationDrawer;
window.hideStationDrawer = hideStationDrawer;
window.getStationLineColor = getStationLineColor;
window.generateTooltipContent = generateTooltipContent;
window.toggleGlobalAlerts = toggleGlobalAlerts;
window.toggleLineAlerts = toggleLineAlerts;
window.toggleStationFeatures = toggleStationFeatures;

// Legacy aliases for backward compatibility
window.showMobileTooltip = showStationDrawer;
window.hideMobileTooltip = hideStationDrawer;
window.showDesktopDrawer = showStationDrawer;
window.hideDesktopDrawer = hideStationDrawer;